import React, { useState } from 'react';
import { Search, MapPin, ChevronDown, Star, ShoppingCart } from 'lucide-react';

interface Product {
  id: string;
  name: string;
  image: string;
  price: number;
  unit: string;
  rating: number;
  reviewCount: number;
  location: string;
  isBestSeller: boolean;
  isOrganic: boolean;
  inStock: boolean;
  codAvailable: boolean;
  quantityOptions: string[];
}

const MOCK_PRODUCTS: Product[] = [
  {
    id: '1',
    name: 'Premium Biogas Units',
    image: '📦',
    price: 5255,
    unit: 'unit',
    rating: 4.5,
    reviewCount: 245,
    location: 'SAUBHAGYA Biocamp Incub...',
    isBestSeller: true,
    isOrganic: true,
    inStock: true,
    codAvailable: true,
    quantityOptions: ['1 liter', '5 liter']
  },
  {
    id: '2',
    name: 'Organic Chocolates',
    image: '🍫',
    price: 120,
    unit: 'pack',
    rating: 4.8,
    reviewCount: 189,
    location: 'SAUBHAGYA Biocamp Incub...',
    isBestSeller: true,
    isOrganic: true,
    inStock: true,
    codAvailable: true,
    quantityOptions: ['1 liter', '2 liter']
  },
  {
    id: '3',
    name: 'AgriRight (Insurer)',
    image: '🥜',
    price: 22,
    unit: 'unit',
    rating: 4.3,
    reviewCount: 156,
    location: 'SAUBHAGYA Biocamp Incub...',
    isBestSeller: true,
    isOrganic: true,
    inStock: true,
    codAvailable: true,
    quantityOptions: ['1 liter', '3 liter']
  },
  {
    id: '4',
    name: 'Organic Granules',
    image: '💧',
    price: 108.91,
    unit: 'pack',
    rating: 4.6,
    reviewCount: 203,
    location: 'SAUBHAGYA Biocamp Incub...',
    isBestSeller: true,
    isOrganic: true,
    inStock: true,
    codAvailable: true,
    quantityOptions: ['1 liter', '4 liter']
  },
  {
    id: '5',
    name: 'Organic Liquids',
    image: '🧪',
    price: 12,
    unit: 'liter',
    rating: 4.4,
    reviewCount: 178,
    location: 'SAUBHAGYA Biocamp Incub...',
    isBestSeller: true,
    isOrganic: true,
    inStock: true,
    codAvailable: true,
    quantityOptions: ['1 liter', '5 liter']
  },
  {
    id: '6',
    name: 'Premium Pellets',
    image: '🪵',
    price: 21,
    unit: 'kg',
    rating: 4.7,
    reviewCount: 267,
    location: 'SAUBHAGYA Biocamp Incub...',
    isBestSeller: true,
    isOrganic: true,
    inStock: true,
    codAvailable: true,
    quantityOptions: ['1 liter', '10 liter']
  },
  {
    id: '7',
    name: 'Organic Fertilizers',
    image: '🌱',
    price: 25,
    unit: 'kg',
    rating: 4.9,
    reviewCount: 312,
    location: 'SAUBHAGYA Biocamp Incub...',
    isBestSeller: true,
    isOrganic: true,
    inStock: true,
    codAvailable: true,
    quantityOptions: ['1 liter', '20 liter']
  },
  {
    id: '8',
    name: 'Biogas Slurry',
    image: '🧴',
    price: 18,
    unit: 'liter',
    rating: 4.5,
    reviewCount: 198,
    location: 'SAUBHAGYA Biocamp Incub...',
    isBestSeller: true,
    isOrganic: true,
    inStock: true,
    codAvailable: true,
    quantityOptions: ['1 liter', '15 liter']
  },
  {
    id: '9',
    name: 'Liquid LegF (Organic)',
    image: '🔥',
    price: 26,
    unit: 'liter',
    rating: 4.2,
    reviewCount: 142,
    location: 'SAUBHAGYA Biocamp Incub...',
    isBestSeller: true,
    isOrganic: true,
    inStock: true,
    codAvailable: true,
    quantityOptions: ['1 liter', '25 liter']
  }
];

const MarketplaceLanding: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All Categories');
  const [selectedSeller, setSelectedSeller] = useState('All Sellers');
  const [selectedLocation, setSelectedLocation] = useState('All Locations');

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - Responsive */}
      <header className="bg-[#1B6B3C] h-16 flex items-center justify-between px-5 md:px-10 lg:px-20 sticky top-0 z-50">
        <div className="flex items-center gap-2">
          <span className="text-lg md:text-xl">🌿</span>
          <span className="text-white text-base md:text-lg font-semibold tracking-wide">SAUBHAGYA</span>
        </div>
        <button className="bg-[#FF8C00] hover:bg-[#E67E00] text-white px-4 md:px-6 py-2 rounded-md font-medium transition-all text-sm md:text-base">
          Contact Us
        </button>
      </header>

      {/* Hero Section - Responsive */}
      <section className="bg-[#1B6B3C] px-5 md:px-10 lg:px-20 py-10 md:py-14 lg:py-16">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-white text-2xl md:text-4xl lg:text-[44px] font-bold leading-tight mb-3 md:mb-4">
            Premium Biogas Products & Solutions
          </h1>
          <p className="text-gray-200 text-sm md:text-base lg:text-[17px] leading-relaxed max-w-2xl mx-auto px-4 md:px-0">
            Quality biogas products, accessories, and expert guidance from verified sellers. Find your solution for sustainable energy and agricultural solutions.
          </p>
        </div>

        {/* Stats Cards - Responsive Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 lg:gap-6 mt-8 md:mt-10 lg:mt-12 max-w-5xl mx-auto">
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 md:p-5 lg:p-6 text-center">
            <div className="text-white text-2xl md:text-3xl lg:text-[36px] font-bold mb-1">500+</div>
            <div className="text-gray-200 text-[10px] md:text-[11px] uppercase tracking-wider">VERIFIED PRODUCTS</div>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 md:p-5 lg:p-6 text-center">
            <div className="text-white text-2xl md:text-3xl lg:text-[36px] font-bold mb-1">150+</div>
            <div className="text-gray-200 text-[10px] md:text-[11px] uppercase tracking-wider">CERTIFIED SELLERS</div>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 md:p-5 lg:p-6 text-center">
            <div className="text-white text-2xl md:text-3xl lg:text-[36px] font-bold mb-1">10K+</div>
            <div className="text-gray-200 text-[10px] md:text-[11px] uppercase tracking-wider">HAPPY BUYERS</div>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 md:p-5 lg:p-6 text-center">
            <div className="text-white text-2xl md:text-3xl lg:text-[36px] font-bold mb-1">24/7</div>
            <div className="text-gray-200 text-[10px] md:text-[11px] uppercase tracking-wider">SUPPORT</div>
          </div>
        </div>
      </section>

      {/* Search & Filter Section - Responsive */}
      <section className="bg-white px-5 md:px-10 lg:px-20 py-4 md:py-5 lg:py-6 shadow-sm">
        <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3 md:gap-4">
          {/* Search Input */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 md:w-5 md:h-5" />
            <input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 md:pl-11 pr-4 py-2 md:py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1B6B3C] focus:border-transparent text-sm md:text-base"
            />
          </div>

          {/* Filters Row - Stacked on mobile */}
          <div className="grid grid-cols-2 md:flex md:flex-row gap-2 md:gap-4">
            {/* Category Dropdown */}
            <div className="relative">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="appearance-none w-full px-3 md:px-4 py-2 md:py-2.5 pr-8 md:pr-10 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-[#1B6B3C] focus:border-transparent cursor-pointer text-sm md:text-base md:min-w-[180px]"
              >
                <option>All Categories</option>
                <option>Biogas Units</option>
                <option>Organic Products</option>
                <option>Fertilizers</option>
              </select>
              <ChevronDown className="absolute right-2 md:right-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 md:w-5 md:h-5 pointer-events-none" />
            </div>

            {/* Seller Dropdown */}
            <div className="relative">
              <select
                value={selectedSeller}
                onChange={(e) => setSelectedSeller(e.target.value)}
                className="appearance-none w-full px-3 md:px-4 py-2 md:py-2.5 pr-8 md:pr-10 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-[#1B6B3C] focus:border-transparent cursor-pointer text-sm md:text-base md:min-w-[160px]"
              >
                <option>All Sellers</option>
                <option>Verified Sellers</option>
                <option>Top Rated</option>
              </select>
              <ChevronDown className="absolute right-2 md:right-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 md:w-5 md:h-5 pointer-events-none" />
            </div>

            {/* Location Dropdown */}
            <div className="relative col-span-2 md:col-span-1">
              <select
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
                className="appearance-none w-full px-3 md:px-4 py-2 md:py-2.5 pr-8 md:pr-10 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-[#1B6B3C] focus:border-transparent cursor-pointer text-sm md:text-base md:min-w-[180px]"
              >
                <option>All Locations</option>
                <option>Delhi</option>
                <option>Mumbai</option>
                <option>Bangalore</option>
              </select>
              <ChevronDown className="absolute right-2 md:right-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 md:w-5 md:h-5 pointer-events-none" />
            </div>

            {/* Search Button */}
            <button className="col-span-2 md:col-span-1 bg-[#FF8C00] hover:bg-[#E67E00] text-white px-6 md:px-8 py-2 md:py-2.5 rounded-md font-semibold transition-all text-sm md:text-base">
              Search
            </button>
          </div>
        </div>
      </section>

      {/* Product Grid Section - Responsive */}
      <section className="px-5 md:px-10 lg:px-20 py-8 md:py-10 lg:py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5 lg:gap-6">
          {MOCK_PRODUCTS.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      </section>

      {/* Footer - Responsive */}
      <footer className="bg-[#1A2332] px-5 md:px-10 lg:px-20 py-8 md:py-10 lg:py-12">
        <div className="max-w-6xl mx-auto">
          {/* Logo & Description */}
          <div className="text-center mb-6 md:mb-8">
            <div className="flex items-center justify-center gap-2 mb-2 md:mb-3">
              <span className="text-xl md:text-2xl">🌿</span>
              <span className="text-white text-lg md:text-xl font-semibold">SAUBHAGYA B2B Marketplace</span>
            </div>
            <p className="text-gray-400 text-xs md:text-sm max-w-2xl mx-auto px-4 md:px-0">
              Your trusted platform for premium biogas and farming quality agricultural solutions
            </p>
          </div>

          {/* Footer Links */}
          <div className="flex items-center justify-center gap-4 md:gap-6 mb-6 md:mb-8 flex-wrap">
            <a href="#" className="text-gray-400 hover:text-white text-xs md:text-sm transition-colors">About Us</a>
            <a href="#" className="text-gray-400 hover:text-white text-xs md:text-sm transition-colors">Contact</a>
            <a href="#" className="text-gray-400 hover:text-white text-xs md:text-sm transition-colors">Terms & Conditions</a>
            <a href="#" className="text-gray-400 hover:text-white text-xs md:text-sm transition-colors">Privacy Policy</a>
            <a href="#" className="text-gray-400 hover:text-white text-xs md:text-sm transition-colors">FAQ</a>
            <a href="#" className="text-gray-400 hover:text-white text-xs md:text-sm transition-colors">Return Policy</a>
          </div>

          {/* Copyright */}
          <div className="text-center text-gray-500 text-[10px] md:text-xs pt-4 md:pt-6 border-t border-gray-700">
            Powered by Dhanush Healthcare Systems
          </div>
        </div>
      </footer>
    </div>
  );
};

interface ProductCardProps {
  product: Product;
}

const ProductCard: React.FC<ProductCardProps> = ({ product }) => {
  const [selectedQuantity, setSelectedQuantity] = useState(product.quantityOptions[0]);

  return (
    <div className="bg-[#E8F5E9] rounded-xl p-4 shadow-[0_2px_8px_rgba(0,0,0,0.08)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.15)] transition-all duration-200 hover:-translate-y-1 relative">
      {/* Best Seller Badge */}
      {product.isBestSeller && (
        <div className="absolute top-4 left-4 bg-[#FF8C00] text-white text-[11px] font-semibold px-2.5 py-1 rounded z-10">
          Best Sell
        </div>
      )}

      {/* Product Image */}
      <div className="flex items-center justify-center h-44 mb-4 text-7xl">
        {product.image}
      </div>

      {/* Product Info */}
      <div className="space-y-2">
        {/* Title */}
        <h3 className="text-[#2C3E50] font-semibold text-base leading-tight">
          {product.name}
        </h3>

        {/* Location */}
        <div className="flex items-center gap-1.5 text-gray-600 text-[13px]">
          <MapPin className="w-3.5 h-3.5" />
          <span className="truncate">{product.location}</span>
        </div>

        {/* COD Available */}
        {product.codAvailable && (
          <div className="flex items-center gap-1.5 text-gray-600 text-[13px]">
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <span>COD Available</span>
          </div>
        )}

        {/* Price */}
        <div className="flex items-baseline gap-1.5 pt-1">
          <span className="text-[#1B6B3C] font-bold text-[22px]">
            ₹{product.price.toFixed(2)}
          </span>
          <span className="text-gray-500 text-sm">
            INR 1 / {product.unit}
          </span>
        </div>

        {/* Rating */}
        <div className="flex items-center gap-1.5">
          <div className="flex items-center">
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                className={`w-3.5 h-3.5 ${
                  i < Math.floor(product.rating)
                    ? 'text-[#FFC107] fill-[#FFC107]'
                    : 'text-gray-300'
                }`}
              />
            ))}
          </div>
          <span className="text-gray-500 text-[13px]">({product.reviewCount})</span>
        </div>

        {/* Tags */}
        <div className="flex items-center gap-2 pt-1">
          {product.isOrganic && (
            <span className="bg-[#D4EDDA] text-[#1B6B3C] text-[11px] font-medium px-3 py-1 rounded-full">
              ORGANIC
            </span>
          )}
          {product.inStock && (
            <span className="bg-[#D4EDDA] text-[#1B6B3C] text-[11px] font-medium px-3 py-1 rounded-full">
              IN STOCK
            </span>
          )}
        </div>

        {/* Quantity Selector */}
        <div className="flex items-center gap-2 pt-2">
          {product.quantityOptions.map((option) => (
            <button
              key={option}
              onClick={() => setSelectedQuantity(option)}
              className={`flex-1 px-3 py-1.5 text-[13px] font-medium rounded-md border transition-all ${
                selectedQuantity === option
                  ? 'bg-[#1B6B3C] text-white border-[#1B6B3C]'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-[#1B6B3C]'
              }`}
            >
              {option}
            </button>
          ))}
        </div>

        {/* Add to Cart Button */}
        <button className="w-full bg-[#1B6B3C] hover:bg-[#155A31] text-white font-semibold py-3 rounded-md transition-all flex items-center justify-center gap-2 mt-3">
          <ShoppingCart className="w-4 h-4" />
          ADD TO CART
        </button>
      </div>
    </div>
  );
};

export default MarketplaceLanding;
